<template>
  <div
    v-if="show"
    class="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 overflow-y-auto"
  >
    <div class="bg-white rounded-t-2xl sm:rounded-lg w-full max-w-3xl p-4 sm:p-6 my-0 sm:my-8 max-h-[92vh] overflow-y-auto shadow-xl">
      <div class="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 class="font-semibold text-lg text-gray-900">{{ $t('smm.etsyGen.title') }}</h3>
          <p class="text-sm text-gray-500 mt-0.5">{{ $t('smm.etsyGen.subtitle') }}</p>
        </div>
        <button
          type="button"
          class="shrink-0 text-gray-400 hover:text-gray-700 text-xl leading-none"
          :aria-label="$t('common.close')"
          @click="close"
        >
          ×
        </button>
      </div>

      <!-- FORM -->
      <div v-if="step === 'form'" class="space-y-4">
        <div>
          <label class="text-sm text-gray-600">{{ $t('smm.etsyGen.network') }}</label>
          <select
            v-model="form.social_network_id"
            class="w-full border rounded-lg p-2.5 mt-1 text-base sm:text-sm bg-white"
          >
            <option :value="null" disabled>{{ $t('common.select_placeholder') }}</option>
            <option v-for="n in networks" :key="n.id" :value="n.id">
              {{ n.name }}<span v-if="n.shop"> — {{ n.shop.name }}</span>
            </option>
          </select>
          <p v-if="!loadingNetworks && !networks.length" class="text-xs text-red-600 mt-1">
            {{ $t('smm.etsyGen.no_networks') }}
          </p>
        </div>

        <div>
          <div class="flex items-center justify-between">
            <label class="text-sm text-gray-600">{{ $t('smm.etsyGen.photos') }}</label>
            <span class="text-xs text-gray-400">{{ images.length }}/{{ maxImages }}</span>
          </div>
          <div class="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-1.5">
            <div
              v-for="(img, i) in images"
              :key="img.url"
              class="relative aspect-square rounded-md overflow-hidden border bg-gray-100"
            >
              <img :src="img.url" :alt="`photo ${i + 1}`" class="w-full h-full object-cover" />
              <button
                type="button"
                class="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-600 text-white text-xs leading-none"
                @click="removeImage(i)"
              >
                ×
              </button>
            </div>
            <label
              v-if="images.length < maxImages"
              class="aspect-square rounded-md border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-500 cursor-pointer text-sm text-center px-1"
            >
              + {{ $t('smm.etsyGen.add_photo') }}
              <input type="file" accept="image/*" multiple class="hidden" @change="onFiles" />
            </label>
          </div>
        </div>

        <div>
          <label class="text-sm text-gray-600">{{ $t('smm.etsyGen.description') }}</label>
          <textarea
            v-model="form.description"
            rows="4"
            class="w-full border rounded-lg p-2.5 mt-1 text-base sm:text-sm resize-y"
            :placeholder="$t('smm.etsyGen.description_placeholder')"
          />
        </div>

        <p v-if="error" class="text-sm text-red-600 whitespace-pre-line">{{ error }}</p>

        <div class="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2 border-t border-gray-100">
          <button
            type="button"
            class="w-full sm:w-auto px-4 py-2.5 text-gray-600 rounded-lg hover:bg-gray-50"
            @click="close"
          >
            {{ $t('common.cancel') }}
          </button>
          <button
            type="button"
            class="w-full sm:w-auto px-4 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
            :disabled="generating || !form.social_network_id"
            @click="generate"
          >
            {{ generating ? $t('smm.etsyGen.generating') : $t('smm.etsyGen.generate') }}
          </button>
        </div>
      </div>

      <!-- RESULT -->
      <div v-else class="space-y-4">
        <div
          class="rounded-lg border p-3"
          :class="saved ? 'border-blue-200 bg-blue-50/60' : 'border-green-200 bg-green-50/60'"
        >
          <p class="text-sm" :class="saved ? 'text-blue-800' : 'text-green-800'">
            {{ saved ? $t('smm.etsyGen.saved_hint') : $t('smm.etsyGen.result_hint') }}
          </p>
        </div>

        <div v-if="previewImages.length" class="flex flex-wrap gap-2">
          <div
            v-for="(url, i) in previewImages"
            :key="url"
            class="w-20 h-20 rounded-md overflow-hidden border border-gray-200 bg-gray-100"
          >
            <img :src="url" :alt="`photo ${i + 1}`" class="w-full h-full object-cover" />
          </div>
        </div>

        <div class="space-y-2">
          <div
            v-for="field in fields"
            :key="field.key"
            class="rounded-lg border border-gray-200 bg-white p-3"
          >
            <div class="flex items-center justify-between gap-2 mb-1">
              <p class="text-[11px] uppercase tracking-wide text-gray-400 truncate">{{ field.label }}</p>
              <div class="shrink-0 flex items-center gap-1.5">
                <span v-if="copiedKey === field.key" class="text-[10px] text-green-600">{{ $t('common.copied') }}</span>
                <button
                  type="button"
                  class="text-gray-400 hover:text-gray-800"
                  :title="$t('common.copy')"
                  @click="copyField(field.value, field.key)"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                </button>
              </div>
            </div>
            <p class="text-sm text-gray-800 whitespace-pre-wrap break-words">{{ field.value || '—' }}</p>
          </div>
        </div>

        <details class="rounded-lg border border-gray-200 bg-gray-50">
          <summary class="cursor-pointer select-none px-3 py-2 text-xs uppercase tracking-wide text-gray-500">
            {{ $t('smm.etsyGen.raw_json') }}
          </summary>
          <pre class="px-3 pb-3 overflow-x-auto text-xs whitespace-pre-wrap">{{ pretty(result) }}</pre>
        </details>

        <p v-if="error" class="text-sm text-red-600 whitespace-pre-line">{{ error }}</p>

        <div class="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2 border-t border-gray-100">
          <button
            v-if="!isExisting"
            type="button"
            class="w-full sm:w-auto px-4 py-2.5 text-gray-600 rounded-lg hover:bg-gray-50"
            @click="step = 'form'"
          >
            {{ $t('smm.etsyGen.back_to_form') }}
          </button>
          <button
            v-if="!saved"
            type="button"
            class="w-full sm:w-auto px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            :disabled="saving"
            @click="save"
          >
            {{ saving ? $t('common.saving') : $t('smm.etsyGen.save') }}
          </button>
          <button
            type="button"
            class="w-full sm:w-auto px-4 py-2.5 rounded-lg"
            :class="saved ? 'bg-blue-600 text-white hover:bg-blue-700' : 'text-gray-600 hover:bg-gray-50'"
            @click="close"
          >
            {{ $t('common.close') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const MAX_IMAGES = 5

export default {
  name: 'EtsyListingModal',
  props: {
    show: { type: Boolean, default: false },
    listing: { type: Object, default: null },
  },
  emits: ['close', 'saved'],
  data: () => ({
    maxImages: MAX_IMAGES,
    step: 'form',
    networks: [],
    loadingNetworks: false,
    form: {
      social_network_id: null,
      description: '',
    },
    images: [],
    generating: false,
    saving: false,
    saved: false,
    isExisting: false,
    error: null,
    result: null,
    listingImageUrls: [],
    copiedKey: null,
    copiedTimer: null,
  }),
  computed: {
    fields() {
      return this.flattenFields(this.result)
    },
    previewImages() {
      if (this.isExisting) return this.listingImageUrls
      return this.images.map((img) => img.url)
    },
  },
  watch: {
    show(value) {
      if (value) {
        if (this.listing) {
          this.viewExisting(this.listing)
        } else {
          this.reset()
          this.loadNetworks()
        }
      } else {
        this.clearImages()
      }
    },
  },
  methods: {
    reset() {
      this.step = 'form'
      this.error = null
      this.result = null
      this.generating = false
      this.saving = false
      this.saved = false
      this.isExisting = false
      this.form = { social_network_id: null, description: '' }
      this.clearImages()
    },
    viewExisting(listing) {
      this.reset()
      this.isExisting = true
      this.saved = true
      this.result = listing.result ?? null
      this.listingImageUrls = Array.isArray(listing.image_urls) ? listing.image_urls : []
      this.form.description = listing.description || ''
      this.form.social_network_id = listing.social_network_id || null
      this.step = 'result'
    },
    clearImages() {
      this.images.forEach((img) => URL.revokeObjectURL(img.url))
      this.images = []
    },
    async loadNetworks() {
      this.loadingNetworks = true
      try {
        const { data } = await axios.get('/api/v1/smm/social-networks')
        const all = Array.isArray(data?.data) ? data.data : []
        this.networks = all.filter(
          (n) => String(n.platform?.slug || '').toLowerCase() === 'etsy',
        )
        if (this.networks.length === 1) {
          this.form.social_network_id = this.networks[0].id
        }
      } catch (e) {
        this.networks = []
        this.error = e.response?.data?.message || e.message || this.$t('common.error')
      } finally {
        this.loadingNetworks = false
      }
    },
    onFiles(event) {
      const files = Array.from(event.target.files || [])
      for (const file of files) {
        if (this.images.length >= MAX_IMAGES) break
        if (!file.type.startsWith('image/')) continue
        this.images.push({ file, url: URL.createObjectURL(file) })
      }
      event.target.value = ''
    },
    removeImage(index) {
      const [removed] = this.images.splice(index, 1)
      if (removed) URL.revokeObjectURL(removed.url)
    },
    async generate() {
      if (!this.form.social_network_id || this.generating) return
      this.generating = true
      this.error = null
      try {
        const fd = new FormData()
        fd.append('social_network_id', String(this.form.social_network_id))
        if (this.form.description.trim()) {
          fd.append('description', this.form.description.trim())
        }
        this.images.forEach((img, i) => fd.append(`images[${i}]`, img.file))

        const { data } = await axios.post('/api/v1/smm/etsy-listings/generate', fd)
        this.result = data?.data?.result ?? null
        this.saved = false
        this.step = 'result'
      } catch (e) {
        this.error = this.formatError(e)
      } finally {
        this.generating = false
      }
    },
    async save() {
      if (!this.result || this.saving || this.saved) return
      this.saving = true
      this.error = null
      try {
        const fd = new FormData()
        if (this.form.social_network_id) {
          fd.append('social_network_id', String(this.form.social_network_id))
        }
        if (this.form.description.trim()) {
          fd.append('description', this.form.description.trim())
        }
        fd.append('result', JSON.stringify(this.result))
        this.images.forEach((img, i) => fd.append(`images[${i}]`, img.file))

        await axios.post('/api/v1/smm/etsy-listings', fd)
        this.saved = true
        this.$emit('saved')
      } catch (e) {
        this.error = this.formatError(e)
      } finally {
        this.saving = false
      }
    },
    formatError(e) {
      const data = e.response?.data
      if (data?.message) return data.message
      if (data?.errors) return Object.values(data.errors).flat().join('\n')
      return e.message || this.$t('common.error')
    },
    pretty(v) {
      try {
        return JSON.stringify(v, null, 2)
      } catch (_) {
        return String(v)
      }
    },
    isDescriptionKey(key) {
      return /description|desc|body|about|details|overview|copy|text/i.test(String(key))
    },
    joinDeep(value) {
      if (value == null) return ''
      if (Array.isArray(value)) {
        return value.map((v) => this.joinDeep(v)).filter((s) => s !== '').join('\n\n')
      }
      if (typeof value === 'object') {
        return Object.values(value).map((v) => this.joinDeep(v)).filter((s) => s !== '').join('\n\n')
      }
      return String(value).trim()
    },
    humanizeKey(path) {
      return String(path)
        .split('.')
        .map((seg) =>
          seg
            .replace(/\[(\d+)\]/g, ' $1')
            .replace(/[_-]+/g, ' ')
            .trim()
            .replace(/\b\w/g, (c) => c.toUpperCase()),
        )
        .join(' · ')
    },
    flattenFields(value, prefix = '') {
      const out = []
      if (value == null) return out

      if (Array.isArray(value)) {
        const allPrimitive = value.every((v) => v == null || typeof v !== 'object')
        if (allPrimitive) {
          const items = value.filter((v) => v != null && String(v).trim() !== '')
          const looksLikeTags = items.some((v) => String(v).trim().startsWith('#'))
          out.push({
            key: prefix || 'items',
            label: this.humanizeKey(prefix || 'items'),
            value: items.join(looksLikeTags ? ' ' : '\n'),
          })
        } else {
          value.forEach((v, i) => {
            out.push(...this.flattenFields(v, `${prefix}[${i + 1}]`))
          })
        }
        return out
      }

      if (typeof value === 'object') {
        for (const [k, v] of Object.entries(value)) {
          const label = prefix ? `${prefix}.${k}` : k
          if (v != null && typeof v === 'object') {
            if (this.isDescriptionKey(k)) {
              // Merge all nested parts of the description into a single text block.
              out.push({
                key: label,
                label: this.humanizeKey(label),
                value: this.joinDeep(v),
              })
            } else {
              out.push(...this.flattenFields(v, label))
            }
          } else {
            out.push({
              key: label,
              label: this.humanizeKey(label),
              value: v == null ? '' : String(v),
            })
          }
        }
        return out
      }

      out.push({
        key: prefix || 'value',
        label: this.humanizeKey(prefix || 'value'),
        value: String(value),
      })
      return out
    },
    async copyField(text, key) {
      const value = text == null ? '' : String(text)
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(value)
        } else {
          const ta = document.createElement('textarea')
          ta.value = value
          ta.style.position = 'fixed'
          ta.style.opacity = '0'
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          document.body.removeChild(ta)
        }
        this.copiedKey = key
        if (this.copiedTimer) clearTimeout(this.copiedTimer)
        this.copiedTimer = setTimeout(() => {
          if (this.copiedKey === key) this.copiedKey = null
        }, 1500)
      } catch (_) {
        this.error = this.$t('common.copy_failed')
      }
    },
    close() {
      this.$emit('close')
    },
  },
  beforeUnmount() {
    this.clearImages()
    if (this.copiedTimer) clearTimeout(this.copiedTimer)
  },
}
</script>
